import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Card, 
  Tag, 
  Button, 
  Modal, 
  Rate, 
  Input,
  message,
  Select,
  Space,
  Statistic,
  Row,
  Col,
  Badge,
  Empty,
  Spin,
  Collapse,
  Tooltip,
  Segmented
} from 'antd';
import { 
  StarOutlined, 
  CommentOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FileTextOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  LikeFilled,
  DislikeFilled,
  UnorderedListOutlined,
  ApartmentOutlined,
  CodeOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Generic Q&A History component.
 *
 * Props:
 *   agentName       {string}   Display label, e.g. "Manager Agent"
 *   fetchHistory    {Function} () => Promise<{ records: [] }>
 *   fetchStatistics {Function} () => Promise<{ stats: {} }>
 *   submitFeedback  {Function} (qaId, feedback) => Promise
 *   markAnswer      {Function} (qaId, isCorrect) => Promise  (optional)
 */
function QAHistory({
  agentName = 'Agent',
  fetchHistory,
  fetchStatistics,
  submitFeedback,
  markAnswer,
  hideHeader = false,
}) {
  const [history, setHistory] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [selectedQA, setSelectedQA] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'conversation'
  const [verifyingIds, setVerifyingIds] = useState(new Set());

  useEffect(() => {
    loadHistory();
    loadStatistics();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await fetchHistory();
      setHistory(data.records || []);
    } catch (error) {
      message.error('Failed to load Q&A history');
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await fetchStatistics();
      setStatistics(data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFeedbackClick = (qa) => {
    setSelectedQA(qa);
    setRating(qa.feedback?.rating || 0);
    setComment(qa.feedback?.comment || '');
    setFeedbackModalVisible(true);
  };

  const handleSubmitFeedback = async () => {
    if (!rating) {
      message.warning('Please provide a rating');
      return;
    }

    try {
      await submitFeedback(selectedQA.id, comment || (rating >= 4 ? 'correct' : 'incorrect'));
      message.success('Feedback submitted successfully');
      setFeedbackModalVisible(false);
      loadHistory();
      loadStatistics();
    } catch (error) {
      message.error('Failed to submit feedback');
      console.error('Error submitting feedback:', error);
    }
  };

  const handleToggleVerified = async (qa) => {
    if (!markAnswer) return;
    const isCurrentlyVerified = qa.verified_by_pic === 1;
    const newVerified = isCurrentlyVerified ? 0 : 1;
    setVerifyingIds(prev => new Set(prev).add(qa.id));
    try {
      await markAnswer(qa.id, !isCurrentlyVerified);
      setHistory(prev => prev.map(item => item.id === qa.id ? { ...item, verified_by_pic: newVerified } : item));
      message.success(isCurrentlyVerified ? 'Answer un-verified' : 'Answer verified & added to gallery');
      loadStatistics();
    } catch (error) {
      message.error('Failed to update verification');
      console.error('Error toggling verification:', error);
    } finally {
      setVerifyingIds(prev => { const next = new Set(prev); next.delete(qa.id); return next; });
    }
  };

  const handleSelectQA = (qa) => {
    setSelectedQA(qa);
  };

  // Filter Q&A records
  const filteredHistory = history.filter((qa) => {
    const matchesSearch = !searchText || 
      qa.question?.toLowerCase().includes(searchText.toLowerCase()) ||
      qa.answer?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'liked' && qa.feedback_type === 'like') ||
      (statusFilter === 'disliked' && qa.feedback_type === 'dislike') ||
      (statusFilter === 'verified' && qa.verified_by_pic === 1) ||
      (statusFilter === 'no-feedback' && !qa.feedback_type && qa.verified_by_pic !== 1);
    
    return matchesSearch && matchesStatus;
  });

  // Group records by session_id for conversation view
  const conversationGroups = useMemo(() => {
    const groups = {};
    filteredHistory.forEach(qa => {
      const sid = qa.session_id || 'no-session';
      if (!groups[sid]) {
        groups[sid] = { session_id: sid, messages: [], first_timestamp: qa.timestamp };
      }
      groups[sid].messages.push(qa);
    });
    // Sort conversations by most recent first, messages within by timestamp asc
    return Object.values(groups)
      .map(g => ({
        ...g,
        messages: g.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
        first_question: g.messages[0]?.question,
        last_timestamp: g.messages[g.messages.length - 1]?.timestamp,
      }))
      .sort((a, b) => new Date(b.last_timestamp) - new Date(a.last_timestamp));
  }, [filteredHistory]);

  const stats = {
    total: history.length,
    liked: history.filter(qa => qa.feedback_type === 'like').length,
    disliked: history.filter(qa => qa.feedback_type === 'dislike').length,
    verified: history.filter(qa => qa.verified_by_pic === 1).length,
    thisWeek: history.filter(qa => {
      const qaDate = new Date(qa.timestamp);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return qaDate >= weekAgo;
    }).length
  };

  return (
    <div style={{ padding: '24px', background: '#ffffff', minHeight: hideHeader ? 'auto' : '100vh' }}>
      {/* Header — hidden when embedded as tab */}
      {!hideHeader && (
        <div style={{ marginBottom: 24 }}>
          <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Title 
                level={3} 
                style={{ 
                  margin: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  color: 'var(--theme-primary)',
                  fontWeight: 'bold'
                }}
              >
                {agentName.toUpperCase()} — Q&A HISTORY
              </Title>
            </Col>
            <Col>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={loadHistory}
                loading={loading}
                size="large"
              >
                REFRESH
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        {/* Statistics Summary Cards */}
        <Row gutter={16}>
          <Col xs={12} sm={6} md={4}>
            <Card 
              size="small"
              style={{
                borderLeft: '4px solid var(--theme-primary)',
                border: '1px solid #d9d9d9',
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Statistic
                title={<Text style={{ fontSize: '12px' }}>Total</Text>}
                value={stats.total}
                prefix={<CommentOutlined style={{ color: 'var(--theme-primary)', fontSize: '18px' }} />}
                valueStyle={{ color: 'var(--theme-primary)', fontSize: '24px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card 
              size="small"
              style={{
                borderLeft: '4px solid #52c41a',
                border: '1px solid #d9d9d9',
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Statistic
                title={<Text style={{ fontSize: '12px' }}>Liked</Text>}
                value={stats.liked}
                prefix={<LikeFilled style={{ color: '#52c41a', fontSize: '18px' }} />}
                valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card 
              size="small"
              style={{
                borderLeft: '4px solid #ff4d4f',
                border: '1px solid #d9d9d9',
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Statistic
                title={<Text style={{ fontSize: '12px' }}>Disliked</Text>}
                value={stats.disliked}
                prefix={<DislikeFilled style={{ color: '#ff4d4f', fontSize: '18px' }} />}
                valueStyle={{ color: '#ff4d4f', fontSize: '24px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card 
              size="small"
              style={{
                borderLeft: '4px solid #1677ff',
                border: '1px solid #d9d9d9',
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Statistic
                title={<Text style={{ fontSize: '12px' }}>Verified</Text>}
                value={stats.verified}
                prefix={<CheckCircleOutlined style={{ color: '#1677ff', fontSize: '18px' }} />}
                valueStyle={{ color: '#1677ff', fontSize: '24px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card 
              size="small"
              style={{
                borderLeft: '4px solid #13c2c2',
                border: '1px solid #d9d9d9',
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Statistic
                title={<Text style={{ fontSize: '12px' }}>This Week</Text>}
                value={stats.thisWeek}
                prefix={<ClockCircleOutlined style={{ color: '#13c2c2', fontSize: '18px' }} />}
                valueStyle={{ color: '#13c2c2', fontSize: '24px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Two Column Layout */}
      <Row gutter={16}>
        {/* Left Column - Q&A List (60%) */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <span>Q&A RECORDS</span>
                <Badge 
                  count={filteredHistory.length} 
                  showZero 
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                />
              </Space>
            }
            style={{ 
              height: 'calc(100vh - 220px)', 
              overflow: 'visible',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              border: '1px solid #d9d9d9',
              borderRadius: '12px'
            }}
            bodyStyle={{ padding: 0, overflow: 'auto', height: 'calc(100% - 57px)' }}
          >
            {/* Filter Controls */}
            <div style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid #d9d9d9',
              background: '#fafafa'
            }}>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <Input
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Search by question or answer..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col style={{ minWidth: 160 }}>
                  <Select
                    placeholder="Filter"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: '100%' }}
                    options={[
                      { label: 'All', value: 'all' },
                      { label: <span><LikeFilled style={{ color: '#52c41a' }} /> Liked</span>, value: 'liked' },
                      { label: <span><DislikeFilled style={{ color: '#ff4d4f' }} /> Disliked</span>, value: 'disliked' },
                      { label: <span><CheckCircleOutlined style={{ color: '#1677ff' }} /> Verified</span>, value: 'verified' },
                      { label: 'No Feedback', value: 'no-feedback' }
                    ]}
                  />
                </Col>
                <Col>
                  <Segmented
                    size="small"
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      { label: <Tooltip title="List view"><UnorderedListOutlined /></Tooltip>, value: 'list' },
                      { label: <Tooltip title="Conversation view"><ApartmentOutlined /></Tooltip>, value: 'conversation' },
                    ]}
                  />
                </Col>
              </Row>
            </div>

            {/* Q&A Cards */}
            <div style={{ padding: '18px', overflow: 'visible', minHeight: '300px' }}>
              <Spin spinning={loading} tip="Loading Q&A history...">
                <div style={{ minHeight: '250px' }}>
                  {!loading && filteredHistory.length === 0 ? (
                    <Empty 
                      description="No Q&A records match your filters" 
                      style={{ padding: '40px 0' }}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ) : !loading && viewMode === 'conversation' ? (
                    /* ── Conversation View ── */
                    <Collapse
                      accordion
                      ghost
                      expandIconPosition="start"
                      items={conversationGroups.map((group) => ({
                        key: group.session_id,
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MessageOutlined style={{ color: 'var(--theme-primary)' }} />
                            <span style={{ fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {group.first_question?.substring(0, 80) || 'Conversation'}
                            </span>
                            <Badge count={group.messages.length} size="small" style={{ backgroundColor: 'var(--theme-primary)' }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {dayjs(group.last_timestamp).fromNow()}
                            </Text>
                          </div>
                        ),
                        children: (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            {group.messages.map((qa) => (
                              <div key={qa.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {/* User question */}
                                <div style={{
                                  padding: '8px 12px', background: '#f0f5ff', borderRadius: '8px 8px 8px 2px',
                                  fontSize: 13, color: '#262626', cursor: 'pointer',
                                }}
                                  onClick={() => handleSelectQA(qa)}>
                                  <Text strong style={{ fontSize: 11, color: '#8c8c8c' }}>You</Text>
                                  <div>{qa.question}</div>
                                </div>
                                {/* Assistant answer */}
                                <div style={{
                                  padding: '8px 12px', background: '#f6ffed', borderRadius: '8px 8px 2px 8px',
                                  fontSize: 13, color: '#262626', cursor: 'pointer',
                                  borderLeft: selectedQA?.id === qa.id ? '3px solid var(--theme-primary)' : '3px solid transparent',
                                }}
                                  onClick={() => handleSelectQA(qa)}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong style={{ fontSize: 11, color: '#8c8c8c' }}>Assistant</Text>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      {qa.feedback_type === 'like' && <LikeFilled style={{ color: '#1677ff', fontSize: 11 }} />}
                                      {qa.feedback_type === 'dislike' && <DislikeFilled style={{ color: '#ff4d4f', fontSize: 11 }} />}
                                      {qa.verified_by_pic === 1 && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 11 }} />}
                                      {qa.user_feedback === 'correct' && <Tag color="success" style={{ fontSize: 10, margin: 0, lineHeight: '16px' }}>✓</Tag>}
                                      {qa.user_feedback === 'incorrect' && <Tag color="error" style={{ fontSize: 10, margin: 0, lineHeight: '16px' }}>✗</Tag>}
                                    </div>
                                  </div>
                                  <div style={{ marginTop: 2 }}>
                                    {qa.answer?.length > 150 ? qa.answer.substring(0, 150) + '...' : qa.answer}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </Space>
                        ),
                      }))}
                    />
                  ) : !loading ? (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      {filteredHistory.map((qa) => (
                        <Card
                          key={qa.id}
                          hoverable
                          onClick={() => handleSelectQA(qa)}
                          style={{
                            border: selectedQA?.id === qa.id ? '2px solid var(--theme-primary)' : '1px solid #d9d9d9',
                            cursor: 'pointer',
                            borderRadius: '12px',
                            transition: 'all 0.3s',
                            boxShadow: selectedQA?.id === qa.id 
                              ? '0 6px 16px rgba(0, 0, 0, 0.25)' 
                              : '0 4px 12px rgba(0, 0, 0, 0.2)',
                            background: '#ffffff',
                            position: 'relative',
                            overflow: 'visible'
                          }}
                          bodyStyle={{ padding: '16px' }}
                          onMouseEnter={(e) => {
                            if (selectedQA?.id !== qa.id) {
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedQA?.id !== qa.id) {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          {/* Verified Toggle - Top Right */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            zIndex: 10
                          }}>
                            <Tooltip title={qa.verified_by_pic === 1 ? 'Un-verify this answer' : 'Verify & add to gallery'}>
                              <Button
                                type="text"
                                size="large"
                                loading={verifyingIds.has(qa.id)}
                                icon={!verifyingIds.has(qa.id) ? <CheckCircleOutlined style={{ fontSize: '20px' }} /> : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVerified(qa);
                                }}
                                disabled={verifyingIds.has(qa.id)}
                                style={{
                                  color: qa.verified_by_pic === 1 ? '#1677ff' : '#8c8c8c',
                                  opacity: qa.verified_by_pic === 1 ? 1 : 0.5,
                                  border: 'none',
                                  boxShadow: 'none'
                                }}
                              />
                            </Tooltip>
                          </div>

                          <Space direction="vertical" size={12} style={{ width: '100%', paddingRight: '60px' }}>
                            {/* Date and Status */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(qa.timestamp).format('YYYY-MM-DD HH:mm')}
                              </Text>
                              {qa.feedback_type === 'like' && (
                                <Tag color="success" icon={<LikeFilled />}>Liked</Tag>
                              )}
                              {qa.feedback_type === 'dislike' && (
                                <Tag color="error" icon={<DislikeFilled />}>Disliked</Tag>
                              )}
                              {qa.verified_by_pic === 1 && (
                                <Tag color="blue" icon={<CheckCircleOutlined />}>Verified</Tag>
                              )}
                              {!qa.feedback_type && qa.verified_by_pic !== 1 && (
                                <Tag color="default">No Feedback</Tag>
                              )}
                            </div>

                            {/* Question */}
                            <Text strong style={{ fontSize: 14 }}>
                              {qa.question}
                            </Text>

                            {/* Answer Preview */}
                            <Text type="secondary" style={{ fontSize: 12, lineHeight: '1.4' }}>
                              {qa.answer?.length > 150 
                                ? qa.answer.substring(0, 150) + '...' 
                                : qa.answer}
                            </Text>
                          </Space>
                        </Card>
                      ))}
                    </Space>
                  ) : null}
                </div>
              </Spin>
            </div>
          </Card>
        </Col>

        {/* Right Column - Preview/Details (40%) */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <Space>
                <EyeOutlined />
                <span>DETAILS</span>
              </Space>
            }
            style={{
              height: 'calc(100vh - 220px)',
              borderLeft: selectedQA ? '2px solid var(--theme-primary)' : '1px solid #d9d9d9',
              transition: 'border-left 0.3s, box-shadow 0.3s',
              boxShadow: selectedQA 
                ? '0 6px 16px rgba(0, 0, 0, 0.25)' 
                : '0 4px 12px rgba(0, 0, 0, 0.2)',
              border: '1px solid #d9d9d9',
              borderRadius: '12px',
              background: '#ffffff'
            }}
            bodyStyle={{ overflow: 'auto', height: 'calc(100% - 57px)', padding: '20px' }}
          >
            {selectedQA ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Header */}
                <div style={{ marginBottom: 12 }}>
                  <Space wrap size={[8, 8]}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(selectedQA.timestamp).format('YYYY-MM-DD HH:mm')}
                    </Text>
                    {selectedQA.feedback_type === 'like' && (
                      <Tag color="success" icon={<LikeFilled />}>Liked</Tag>
                    )}
                    {selectedQA.feedback_type === 'dislike' && (
                      <Tag color="error" icon={<DislikeFilled />}>Disliked</Tag>
                    )}
                    {selectedQA.verified_by_pic === 1 && (
                      <Tag color="blue" icon={<CheckCircleOutlined />}>Verified</Tag>
                    )}
                    {!selectedQA.feedback_type && selectedQA.verified_by_pic !== 1 && (
                      <Tag color="default">No Feedback</Tag>
                    )}
                    {selectedQA.confidence != null && (
                      <Tag color={selectedQA.confidence >= 0.85 ? 'green' : selectedQA.confidence >= 0.7 ? 'gold' : 'red'}>
                        Confidence: {Math.round(selectedQA.confidence * 100)}%
                      </Tag>
                    )}
                  </Space>
                </div>

                {/* Question */}
                <div>
                  <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>QUESTION</Text>
                  <div style={{ 
                    marginTop: 8, 
                    padding: '12px', 
                    background: '#f7fafc', 
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <Text>{selectedQA.question}</Text>
                  </div>
                </div>

                {/* Answer */}
                <div>
                  <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>ANSWER</Text>
                  <div style={{ 
                    marginTop: 8, 
                    padding: '16px', 
                    background: '#f7fafc', 
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {selectedQA.answer}
                    </ReactMarkdown>
                  </div>
                </div>

                {/* SQL Query */}
                {selectedQA.generated_sql && (
                  <div>
                    <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>
                      <CodeOutlined style={{ marginRight: 4 }} />SQL QUERY
                    </Text>
                    <div style={{ 
                      marginTop: 8, 
                      padding: '12px', 
                      background: '#1e1e1e', 
                      borderRadius: '8px',
                      position: 'relative',
                    }}>
                      <Button
                        type="text" size="small"
                        style={{ position: 'absolute', top: 4, right: 4, color: '#8c8c8c' }}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedQA.generated_sql);
                          message.success('SQL copied to clipboard');
                        }}
                      >
                        Copy
                      </Button>
                      <pre style={{ 
                        margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        color: '#d4d4d4', fontSize: 12, fontFamily: 'monospace',
                      }}>
                        {selectedQA.generated_sql}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Data Preview */}
                {selectedQA.fetched_data && (
                  <div>
                    <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>📋 DATA PREVIEW</Text>
                    <div style={{ 
                      marginTop: 8, 
                      maxHeight: '200px', 
                      overflow: 'auto',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}>
                      {(() => {
                        const data = typeof selectedQA.fetched_data === 'string'
                          ? JSON.parse(selectedQA.fetched_data) : selectedQA.fetched_data;
                        if (data?.columns && data?.rows) {
                          return (
                            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#fafafa' }}>
                                  {data.columns.map((col, i) => (
                                    <th key={i} style={{ padding: '6px 8px', borderBottom: '1px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(data.rows || []).slice(0, 10).map((row, ri) => (
                                  <tr key={ri}>
                                    {row.map((cell, ci) => (
                                      <td key={ci} style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                        {cell == null ? <i style={{ color: '#ccc' }}>null</i> : String(cell)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        }
                        return <pre style={{ margin: 0, padding: 12, fontSize: 11 }}>{JSON.stringify(data, null, 2)}</pre>;
                      })()}
                    </div>
                    {(() => {
                      const data = typeof selectedQA.fetched_data === 'string'
                        ? JSON.parse(selectedQA.fetched_data) : selectedQA.fetched_data;
                      return data?.row_count ? (
                        <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                          {data.row_count} total rows{data.rows?.length < data.row_count ? ` (showing first ${data.rows.length})` : ''}
                        </Text>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Tool Trace */}
                {selectedQA.tool_trace && (() => {
                  const trace = typeof selectedQA.tool_trace === 'string'
                    ? JSON.parse(selectedQA.tool_trace) : selectedQA.tool_trace;
                  if (!Array.isArray(trace) || trace.length === 0) return null;
                  return (
                    <div>
                      <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>🔧 TOOL EXECUTION</Text>
                      <div style={{ marginTop: 8 }}>
                        {trace.map((t, i) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                            fontSize: 12, color: '#595959',
                          }}>
                            <span style={{ color: t.status === 'success' ? '#52c41a' : '#ff4d4f' }}>●</span>
                            <span style={{ fontFamily: 'monospace' }}>{t.name}</span>
                            {t.elapsed_ms != null && (
                              <Text type="secondary" style={{ fontSize: 11 }}>{(t.elapsed_ms / 1000).toFixed(1)}s</Text>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Rating */}
                {selectedQA.feedback?.rating && (
                  <div>
                    <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>RATING</Text>
                    <div style={{ marginTop: 8 }}>
                      <Rate disabled value={selectedQA.feedback.rating} />
                    </div>
                  </div>
                )}

                {/* Feedback Comment */}
                {selectedQA.feedback?.comment && (
                  <div>
                    <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>FEEDBACK</Text>
                    <div style={{ 
                      marginTop: 8, 
                      padding: '12px', 
                      background: '#f7fafc', 
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <Text>{selectedQA.feedback.comment}</Text>
                    </div>
                  </div>
                )}
              </Space>
            ) : (
              <Empty
                description="Select a Q&A record to view details"
                style={{ marginTop: '60px' }}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Feedback Modal */}
      <Modal
        title="Provide Feedback"
        open={feedbackModalVisible}
        onOk={handleSubmitFeedback}
        onCancel={() => setFeedbackModalVisible(false)}
        okText="Submit"
      >
        {selectedQA && (
          <>
            <Paragraph strong>Question:</Paragraph>
            <Paragraph>{selectedQA.question}</Paragraph>
            
            <Paragraph strong style={{ marginTop: '16px' }}>Rating:</Paragraph>
            <Rate value={rating} onChange={setRating} />
            
            <Paragraph strong style={{ marginTop: '16px' }}>Comment (optional):</Paragraph>
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add your feedback here..."
              rows={4}
            />
          </>
        )}
      </Modal>
    </div>
  );
}

export default QAHistory;
